/**
 * Permission Helper Utility
 * Helper functions to check permissions based on user and device permissions
 * Based on Flutter InformationController logic
 * @author Based on Flutter implementation
 */

import PermissionCustomerModelController from '../controller/model/PermissionCustomerModelController';
import PermissionBrokerModelController from '../controller/model/PermissionBrokerModelController';

/**
 * Get permission controllers from user data
 * Similar to Flutter InformationController.loadInfo()
 */
export const getPermissionControllers = (user) => {
  if (!user) {
    return {
      customerPerm: null,
      customerDevicePerm: null,
      brokerPerm: null,
      brokerDevicePerm: null,
    };
  }

  // Get customer permissions from user (checks PERMISSIONS property - uppercase)
  const customerPerm = new PermissionCustomerModelController(user);
  
  // Get customer device permissions from first device (checks device or devices property)
  // Device permissions are in user.device[0].PERMISSION or user.devices[0].PERMISSION
  const deviceArray = user.device || user.devices || [];
  const firstDevice = Array.isArray(deviceArray) && deviceArray.length > 0 ? deviceArray[0] : null;
  const customerDevicePerm = PermissionCustomerModelController.fromDevice(firstDevice);
  
  // Get broker permissions from user (checks PERMISSIONS property - uppercase)
  const brokerPerm = new PermissionBrokerModelController(user);
  
  // Get broker device permissions from first device (checks PERMISSION property - uppercase)
  const brokerDevicePerm = PermissionBrokerModelController.fromDevice(firstDevice);

  return {
    customerPerm,
    customerDevicePerm,
    brokerPerm,
    brokerDevicePerm,
  };
};

/**
 * Check if user has both user permission AND device permission for a customer permission
 * AND Logic: Both user.PERMISSIONS and device.PERMISSION must contain the permission
 * 
 * @param {PermissionCustomerModelController} customerPerm - From user.PERMISSIONS
 * @param {PermissionCustomerModelController} customerDevicePerm - From user.device[0].PERMISSION
 * @param {string} permission - The permission constant to check
 * @returns {boolean} - true only if BOTH user and device have the permission
 */
export const hasCustomerPermission = (customerPerm, customerDevicePerm, permission) => {
  if (!customerPerm || !customerDevicePerm) return false;
  
  const hasUserPerm = customerPerm.hasPermission(permission);
  const hasDevicePerm = customerDevicePerm.hasPermission(permission);
  
  // AND logic: both must be true
  return hasUserPerm && hasDevicePerm;
};

/**
 * Check if user has any of the specified customer permissions (both user and device)
 */
export const hasAnyCustomerPermission = (customerPerm, customerDevicePerm, permissions) => {
  if (!customerPerm || !customerDevicePerm) return false;
  
  return permissions.some(permission => 
    hasCustomerPermission(customerPerm, customerDevicePerm, permission)
  );
};

/**
 * Account OR device has this permission.
 * Matches the app cash-in MT5 tab and internal-transfer direction checks.
 */
export const hasCustomerPermissionEither = (customerPerm, customerDevicePerm, permission) => {
  return Boolean(customerPerm?.hasPermission?.(permission)) ||
    Boolean(customerDevicePerm?.hasPermission?.(permission));
};

/**
 * Menu visibility used by the app home screen:
 * the account has any of these permissions AND the device has any of them.
 * The two sides do not have to hold the same flag.
 */
export const hasCustomerPermissionOnEachSide = (customerPerm, customerDevicePerm, permissions) => {
  const list = Array.isArray(permissions) ? permissions : [];
  const onAccount = list.some((permission) => Boolean(customerPerm?.hasPermission?.(permission)));
  const onDevice = list.some((permission) => Boolean(customerDevicePerm?.hasPermission?.(permission)));
  return onAccount && onDevice;
};

/**
 * Check if user has both user permission AND device permission for a broker permission
 * AND Logic: Both user.PERMISSIONS and device.PERMISSION must contain the permission
 * 
 * @param {PermissionBrokerModelController} brokerPerm - From user.PERMISSIONS
 * @param {PermissionBrokerModelController} brokerDevicePerm - From user.device[0].PERMISSION
 * @param {string} permission - The permission constant to check
 * @returns {boolean} - true only if BOTH user and device have the permission
 */
/**
 * Check if the broker account has a permission.
 * Account-level BROKER permissions (admin agent Permissions tab) are the source of truth
 * for the web portal. Device PERMISSION is kept in sync by the API on save, but secondary
 * devices can lag; gating the UI on account permissions avoids false denials.
 *
 * @param {PermissionBrokerModelController} brokerPerm - From user.PERMISSIONS
 * @param {PermissionBrokerModelController} brokerDevicePerm - From user.device[0].PERMISSION
 * @param {string} permission - The permission constant to check
 * @returns {boolean}
 */
export const hasBrokerPermission = (brokerPerm, brokerDevicePerm, permission) => {
  if (!brokerPerm) return false;
  return brokerPerm.hasPermission(permission);
};

/**
 * Admin Permissions-tab switches that hide a feature when checked.
 * The key lives on the account. Checked (present) hides the feature.
 * Unchecked (absent) shows it.
 */
export const isAccountPermissionDisabled = (permController, permission) =>
  Boolean(permController?.hasPermission?.(permission));

/**
 * Check if user can deposit (broker permission)
 */
export const canDeposit = (brokerPerm, brokerDevicePerm) => {
  if (!brokerPerm || !brokerDevicePerm) return false;
  
  return brokerPerm.canDeposit() && brokerDevicePerm.canDeposit();
};

/**
 * Check if user can withdraw (broker permission)
 */
export const canWithdraw = (brokerPerm, brokerDevicePerm) => {
  if (!brokerPerm || !brokerDevicePerm) return false;
  
  return brokerPerm.canWithdraw() && brokerDevicePerm.canWithdraw();
};

/**
 * True when AGENT_WITHDRAWAL_DISABLED is checked on the agent (broker) account.
 * Account-level BROKER permissions (admin agent Permissions tab) are the source of truth.
 */
export const isAgentWithdrawalDisabled = (user) => {
  if (!user) return false;
  const { brokerPerm } = getPermissionControllers(user);
  const perm = PermissionBrokerModelController.PER__AGENT_WITHDRAWAL_DISABLED;
  return Boolean(brokerPerm?.hasPermission(perm));
};

/**
 * Whether the BROKER/agent account may show withdraw actions.
 *
 * - AGENT_WITHDRAWAL_DISABLED checked  → hide withdraw (tables + internal transfer From)
 * - AGENT_WITHDRAWAL_DISABLED unchecked → show withdraw normally
 *
 * Real accounts are unaffected.
 */
export const isBrokerWithdrawalAllowed = (user) => !isAgentWithdrawalDisabled(user);

/**
 * Check if user is a broker
 */
export const isBroker = (user) => {
  if (!user) return false;
  
  // Check TYPE field (might be 2 or 3 for BROKER, or string "BROKER")
  const userType = user.TYPE || user.type || user.userType;
  return userType === 2 || userType === 3 || userType === 'BROKER' || userType === 'IB';
};

/**
* Get raw KYC status value from user object (matches backend KycStatus enum)
* Values:
*  -3 UPDATED_KYC
*  -2 HAS_KYC_ERROR
*  -1 HAS__APPROVED_ERROR
*   0 PENDING
*   1 APPROVED
*   2 VERIFIED
*   3 VERIFIED_NOT_CHECKED
*/
export const getKycStatusValue = (user) => {
  if (!user) return null;

  const raw =
    user.user_information?.IS_KYC_VERIFIED ??
    user.userInformation?.IS_KYC_VERIFIED ??
    user.user_information?.isKycVerified ??
    user.userInformation?.isKycVerified;

  if (raw === undefined || raw === null) return null;

  const num = typeof raw === "number" ? raw : Number(raw);
  return Number.isNaN(num) ? null : num;
};

/**
* Check if user is in "Account Approval Required" stage:
* PENDING (0) or HAS__APPROVED_ERROR (-1)
*/
export const isKycApprovalPending = (user) => {
  const status = getKycStatusValue(user);
  return status === 0 || status === -1;
};

/**
 * Check if email is verified.
 * Backend typically uses IS_EMAIL_VERIFIED:
 * - 1 => verified
 * - 0 => not verified
 */
export const isEmailVerified = (user) => {
  if (!user) return false;

  const raw =
    user.user_information?.IS_EMAIL_VERIFIED ??
    user.userInformation?.IS_EMAIL_VERIFIED ??
    user.user_information?.isEmailVerified ??
    user.userInformation?.isEmailVerified;

  if (raw === undefined || raw === null) return false;

  const num = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isNaN(num)) return num === 1;

  if (typeof raw === "boolean") return raw;
  if (typeof raw === "string") return raw.toLowerCase() === "true";
  return false;
};

/**
 * Check if the user has completed their profile.
 * Mirrors Flutter's `profileCompleted` / `PROFILE_STATUS` checks.
 */
export const isProfileCompleted = (user) => {
  if (!user) return false;

  // Prefer explicit numeric status if available
  const rawStatus = user.PROFILE_STATUS ?? user.profileStatus ?? user.profile_completed;
  if (rawStatus !== undefined && rawStatus !== null) {
    const num = typeof rawStatus === "number" ? rawStatus : Number(rawStatus);
    if (!Number.isNaN(num)) {
      // In backend/mobile, 1 usually means completed
      return num === 1;
    }
  }

  // Fallback booleans or flags on nested objects
  return Boolean(
    user.profileCompleted ||
    user.user_information?.PROFILE_COMPLETED ||
    user.userInformation?.PROFILE_COMPLETED
  );
};

/**
 * Global lock for financial actions (accounts/funds):
 * - Profile NOT completed  → locked
 * - KYC approval pending   → locked
 * - Email NOT verified     → locked
 *
 * When this returns true, user should not be able to:
 * - List/Add accounts
 * - Use Cash In / Internal Transfer / Cash Out
 */
export const isAccountApprovalLocked = (user) => {
  return (
    !isProfileCompleted(user) ||
    isKycApprovalPending(user) ||
    !isEmailVerified(user)
  );
};

/**
* Check if user is KYC verified (final stage: VERIFIED or VERIFIED_NOT_CHECKED)
 */
export const isKycVerified = (user) => {
  if (!user) return false;
  
  const kycStatus = getKycStatusValue(user);
  
  // KycStatus.VERIFIED.value = 2, VERIFIED_NOT_CHECKED.value = 3
  return kycStatus === 2 || kycStatus === 3;
};
